/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import AbstractGeometry from "App/geometry/objects_geometry/abstract_geometry";
import VertexDataDescriptor from "App/geometry/objects_geometry/vertex_data_descriptor";
import RenderObject from "App/geometry/objects/object";
import {mat4, vec3} from "gl-matrix";
import Factory from "App/geometry/objects_geometry/factory";
import Appearance from "App/geometry/material/appearance";

export default
class PointSet extends AbstractGeometry {

    public static TypeName: string = "pointSet";

    public texCoordsDesc: VertexDataDescriptor = null;

    constructor(aObject: RenderObject) {
        super(aObject);
    }

    public static create(aObject: RenderObject) {
        return new PointSet(aObject);
    }

    public clear() {
        super.clear();

        this.texCoordsDesc = null;
    }

    public getIndicesCount(): number {
        return this.getVerticesCount();
    }

    public transform(aTransformation: mat4) {
        this.positionsDesc.data = AbstractGeometry.transformArray(
            <Float32Array>this.positionsDesc.data,
            this.positionsDesc.nofElements,
            aTransformation
        );

        // recompute bbox from the transformed vertices. Transformation of just the min and max simply does not work,
        // because the aabb can be bigger after transformation compared to the local non aabb.
        let aabb = this.computeBBox();
        this.object.getProperties().bboxMin = vec3.clone(aabb.getMin());
        this.object.getProperties().bboxMax = vec3.clone(aabb.getMax());
    }

    public loadData(aBuffer: Uint8Array) {
        let data: any = {
            positions: null,
            texCoords: null
        };

        data.positions = new Float32Array(this.properties.points);

        let repetitionTrafo = this.object.getElement().getRepetitionTransformation();
        if (repetitionTrafo) {
            data.positions =
                AbstractGeometry.transformArray(
                    data.positions,
                    this.properties.vertexCount,
                    new Float32Array(repetitionTrafo)
                );
        }


        // determine the glyph position for the points in the point set from the object appearance
        let appearance: Appearance = this.object.appearance;
        let pointProperties = appearance.pointProperties;

        // create texture coordinates for the pointset
        data.texCoords = new Float32Array((data.positions.length / 3) * 2);
        for (let i = 0; i < data.positions.length / 3; i++) {
            if (pointProperties) {
                data.texCoords[i * 2 + 0] = pointProperties.glyphPos[0];
                data.texCoords[i * 2 + 1] = pointProperties.glyphPos[1];
            } else {
                data.texCoords[i * 2 + 0] = 0;
                data.texCoords[i * 2 + 1] = 0;
            }
        }

        this.setDescriptors(data);

        data = null;
    }

    protected setDescriptors(aData: any) {
        // fill mesh with data
        let positionAttributeSize = this.object.getPositionAttributeSize();
        this.vertexCount = aData.positions.byteLength / (positionAttributeSize * 4);

        this.positionsDesc = new VertexDataDescriptor(
            "positions",
            positionAttributeSize,
            aData.positions.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            aData.positions
        );

        this.texCoordsDesc = new VertexDataDescriptor(
            "texCoords",
            2,
            aData.texCoords.length / 2,
            "FLOAT",
            4,
            0,
            aData.texCoords
        );
    }
}

Factory.register(PointSet);